import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { 
  Search, 
  Filter, 
  Download, 
  RefreshCw,
  Calendar,
  TrendingUp,
  Users,
  FileText,
  Activity,
  Target,
  AlertCircle
} from 'lucide-react';
import EnhancedAnalyticsCharts from './EnhancedAnalyticsCharts';
import EnhancedAnalyticsTables from './EnhancedAnalyticsTables';
import DashboardViewToggle from './DashboardViewToggle';
import DashboardAlerts from './DashboardAlerts';
import { AIInsightsPanel } from './AIInsightsPanel';
import { useComprehensiveAnalytics, useRefreshDashboard, useAggregatedDashboard } from '../../hooks/useDashboardAnalytics';
import type { DateRange } from '../../services/dashboardService';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface EnhancedAnalyticsDashboardProps {
  dateRange?: DateRange;
}

// Helper to get today's date as YYYY-MM-DD in local timezone
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const EnhancedAnalyticsDashboard: React.FC<EnhancedAnalyticsDashboardProps> = ({
  dateRange: initialDateRange
}) => {
  const [viewMode, setViewMode] = useState<'overview' | 'charts' | 'tables'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  // Default to "Today" filter
  const todayStr = getTodayDateString();
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange || { start_date: todayStr, end_date: todayStr });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Use React Query hooks for data fetching
  const { 
    data: analyticsData, 
    isLoading, 
    isError, 
    error 
  } = useComprehensiveAnalytics(dateRange);

  // Fetch aggregated dashboard data with AI insights
  const { 
    data: aggregatedData
  } = useAggregatedDashboard(dateRange);

  const refreshMutation = useRefreshDashboard();

  const handleRefresh = () => {
    refreshMutation.mutate();
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Build query params based on current date range
      const params = new URLSearchParams();
      if (dateRange?.start_date) {
        params.append('start_date', dateRange.start_date);
      }
      if (dateRange?.end_date) {
        params.append('end_date', dateRange.end_date);
      }
      if (dateRange?.days) {
        params.append('days', dateRange.days.toString());
      }
      
      // Get auth token from localStorage (same key as API client)
      const token = localStorage.getItem('access_token');
      
      // Resolve API base URL (same logic as API client)
      const envAny = (import.meta as any).env || {};
      const isProd = !!envAny?.PROD;
      const baseURL = isProd
        ? (envAny.VITE_API_BASE_URL_PROD || envAny.VITE_API_BASE_URL)
        : (envAny.VITE_API_BASE_URL_DEV || envAny.VITE_API_BASE_URL);
      const apiBase = baseURL || 'https://6oi3xs2kzb.execute-api.ap-southeast-1.amazonaws.com';
      
      // Fetch CSV from backend
      const response = await fetch(
        `${apiBase}/api/v1/dashboard/export?${params}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to export dashboard data');
      }
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'dashboard_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export dashboard data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDateRangePreset = (days: number) => {
    setDateRange({ days });
    setShowDatePicker(false);
  };

  // Helper to format date as YYYY-MM-DD in local timezone (not UTC)
  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleToday = () => {
    const today = new Date();
    const todayStr = formatLocalDate(today);
    setDateRange({
      start_date: todayStr,
      end_date: todayStr
    });
    setShowDatePicker(false);
  };

  const handleYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = formatLocalDate(yesterday);
    setDateRange({
      start_date: yesterdayStr,
      end_date: yesterdayStr
    });
    setShowDatePicker(false);
  };

  const handleCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      setDateRange({
        start_date: customStartDate,
        end_date: customEndDate
      });
      setShowDatePicker(false);
    }
  };

  const formatDateRangeDisplay = () => {
    if (dateRange.days) {
      return `Last ${dateRange.days} days`;
    } else if (dateRange.start_date && dateRange.end_date) {
      const start = new Date(dateRange.start_date);
      const end = new Date(dateRange.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      
      // Check if it's today
      if (start.getTime() === today.getTime() && end.getTime() === today.getTime()) {
        return 'Today';
      }
      // Check if it's yesterday
      if (start.getTime() === yesterday.getTime() && end.getTime() === yesterday.getTime()) {
        return 'Yesterday';
      }
      
      const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startStr} - ${endStr}`;
    }
    return 'Last 30 days';
  };

  // Filter time_series data based on selected date range
  const getFilteredTimeSeries = () => {
    if (!analyticsData?.time_series) return [];
    
    // If using specific date range (Today/Yesterday/Custom), filter to only those dates
    if (dateRange.start_date && dateRange.end_date) {
      const startDate = dateRange.start_date;
      const endDate = dateRange.end_date;
      
      return analyticsData.time_series.filter(item => {
        const itemDate = item.date;
        return itemDate >= startDate && itemDate <= endDate;
      });
    }
    
    // For "Last X days", return all data
    return analyticsData.time_series;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="xl" variant="ai" />
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="glass-card p-8 text-center">
          <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-text-primary mb-2">Failed to Load Dashboard</h3>
          <p className="text-text-secondary mb-4">
            {error?.message || 'An error occurred while loading dashboard data'}
          </p>
          <Button onClick={handleRefresh} variant="default">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  // Show empty state if no data
  if (!analyticsData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="glass-card p-8 text-center">
          <Target className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
          <h3 className="text-xl font-bold text-text-primary mb-2">No Data Available</h3>
          <p className="text-text-secondary">
            No analytics data found for the selected date range.
          </p>
        </Card>
      </div>
    );
  }

  // Helper function to get filter period label
  const getFilterPeriodLabel = () => {
    if (dateRange.days) {
      return `Last ${dateRange.days} days`;
    } else if (dateRange.start_date && dateRange.end_date) {
      if (dateRange.start_date === dateRange.end_date) {
        const today = new Date().toISOString().split('T')[0];
        if (dateRange.start_date === today) return 'Today';
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        if (dateRange.start_date === yesterday.toISOString().split('T')[0]) return 'Yesterday';
      }
      return 'Custom range';
    }
    return 'All time';
  };

  // Reusable filter indicator component
  const FilterIndicator = () => (
    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        <span className="text-sm text-blue-700 dark:text-blue-300">
          Showing data for: <strong>{formatDateRangeDisplay()}</strong>
        </span>
      </div>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleRefresh}
        disabled={refreshMutation.isPending}
        className="text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50"
      >
        <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );

  const renderOverviewMode = () => (
    <div className="space-y-6">
      <FilterIndicator />

      {/* AI Insights Panel */}
      {aggregatedData && aggregatedData.ai_insights && (
        <AIInsightsPanel
          insights={aggregatedData.ai_insights}
          generatedAt={aggregatedData.ai_generated_at}
          source={aggregatedData.source}
        />
      )}

      {/* Enhanced Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Requirements */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Requirements</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {analyticsData.overview.total_requirements}
              </p>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{getFilterPeriodLabel()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Requirements */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Active Requirements</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {analyticsData.overview.active_requirements}
              </p>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500 dark:text-gray-400">Currently active</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Submissions */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Uploads</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {analyticsData.overview.total_submissions}
              </p>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{getFilterPeriodLabel()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Average Fitment Score */}
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <TrendingUp className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Avg Fitment Score</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {analyticsData.overview.avg_fitment_score}%
              </p>
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full ${
                  analyticsData.overview.avg_fitment_score >= 80 ? 'bg-green-500' :
                  analyticsData.overview.avg_fitment_score >= 70 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}></div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{getFilterPeriodLabel()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section */}
      {analyticsData.alerts && analyticsData.alerts.length > 0 && (
        <DashboardAlerts alerts={analyticsData.alerts} />
      )}

    </div>
  );

  const renderChartsMode = () => (
    <div className="space-y-6">
      <FilterIndicator />
      <EnhancedAnalyticsCharts
        clientBreakdown={analyticsData.client_breakdown.map(client => ({
          client: client.client_name,
          requirements: client.requirements,
          positions: client.positions || 0,
          submissions: client.submissions,
          avgFitmentScore: client.avg_fitment_score
        }))}
        fitmentScoreDistribution={analyticsData.fitment_score_distribution}
        dayWiseSubmissions={getFilteredTimeSeries()}
        managerWiseSubmissions={analyticsData.manager_performance.map(manager => ({
          manager: manager.manager,
          submissions: manager.submissions
        }))}
        recruiterPerformance={analyticsData.recruiter_performance?.map(rec => ({
          recruiter: rec.recruiter_name,
          manager: rec.manager_name || 'N/A',
          totalSubmissions: rec.total_submissions,
          fitmentScores: rec.fitment_scores || {}
        }))}
        pipelineHealth={analyticsData.pipeline_health}
        periodComparison={analyticsData.period_comparison}
        qualityMetrics={analyticsData.quality_metrics}
        isLoading={isLoading}
      />
    </div>
  );

  const renderTablesMode = () => (
    <div className="space-y-6">
      <FilterIndicator />
      <EnhancedAnalyticsTables
        requirementWiseSubmissions={analyticsData.requirements_analytics.map(req => ({
          requirement: req.requirement,
          submissions: req.submissions,
          avgScore: req.avg_score,
          client: req.client,
          status: req.status as 'active' | 'inactive' | 'closed',
          createdDate: req.created_date,
          lastSubmission: req.last_submission
        }))}
        recruiterPerformance={analyticsData.recruiter_performance.map(rec => ({
          recruiter: rec.recruiter_name,
          manager: rec.manager_name,
          totalSubmissions: rec.total_submissions,
          fitmentScores: rec.fitment_scores
        }))}
        clientBreakdown={analyticsData.client_breakdown.map(client => ({
          client: client.client_name,
          requirements: client.requirements,
          positions: client.positions || 0,
          submissions: client.submissions,
          avgFitmentScore: client.avg_fitment_score
        }))}
        fitmentScoreDistribution={analyticsData.fitment_score_distribution}
        dayWiseSubmissions={getFilteredTimeSeries()}
        managerWiseSubmissions={analyticsData.manager_performance.map(manager => ({
          manager: manager.manager,
          submissions: manager.submissions
        }))}
        isLoading={isLoading}
      />
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Enhanced Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-display-sm font-bold gradient-text">
            Analytics Dashboard
          </h1>
          <p className="text-body-lg text-text-secondary max-w-2xl">
            Comprehensive insights and performance metrics for your recruitment activities
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshMutation.isPending}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center space-x-2"
          >
            <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
          </Button>
        </div>
      </div>

      {/* Enhanced Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* View Mode Toggle */}
        <DashboardViewToggle
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {/* Filters and Search */}
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-48 lg:w-64 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
          <div className="relative" ref={datePickerRef}>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">{formatDateRangeDisplay()}</span>
              <Calendar className="w-4 h-4 sm:hidden" />
            </Button>
            {showDatePicker && (
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filter by Date</h3>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg"
                    >
                      ✕
                    </button>
                  </div>
                  
                  {/* Preset Options */}
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Quick Filters</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={
                          dateRange.start_date && dateRange.end_date && 
                          dateRange.start_date === dateRange.end_date &&
                          dateRange.start_date === formatLocalDate(new Date())
                            ? "default" : "outline"
                        }
                        size="sm"
                        onClick={handleToday}
                        className="text-xs"
                      >
                        Today
                      </Button>
                      <Button
                        variant={
                          dateRange.start_date && dateRange.end_date && 
                          dateRange.start_date === dateRange.end_date &&
                          dateRange.start_date === (() => {
                            const yesterday = new Date();
                            yesterday.setDate(yesterday.getDate() - 1);
                            return formatLocalDate(yesterday);
                          })()
                            ? "default" : "outline"
                        }
                        size="sm"
                        onClick={handleYesterday}
                        className="text-xs"
                      >
                        Yesterday
                      </Button>
                      <Button
                        variant={dateRange.days === 7 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDateRangePreset(7)}
                        className="text-xs"
                      >
                        Last 7 days
                      </Button>
                      <Button
                        variant={dateRange.days === 30 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDateRangePreset(30)}
                        className="text-xs"
                      >
                        Last 30 days
                      </Button>
                      <Button
                        variant={dateRange.days === 90 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDateRangePreset(90)}
                        className="text-xs"
                      >
                        Last 90 days
                      </Button>
                      <Button
                        variant={dateRange.days === 180 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDateRangePreset(180)}
                        className="text-xs"
                      >
                        Last 6 months
                      </Button>
                    </div>
                  </div>

                  {/* Custom Date Range */}
                  <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Custom Range</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Start Date</label>
                        <Input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">End Date</label>
                        <Input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleCustomDateRange}
                        disabled={!customStartDate || !customEndDate}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Apply Custom Range
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'overview' && renderOverviewMode()}
      {viewMode === 'charts' && renderChartsMode()}
      {viewMode === 'tables' && renderTablesMode()}
    </div>
  );
};

export default EnhancedAnalyticsDashboard;